<template>
  <div class="sales-by-payment-method">
    <!-- Header Section -->
    <div class="bg-dark m-b-30">
      <div class="container">
        <div class="row p-b-30 p-t-30">
          <div class="col-md-12 text-center">
            <h2 class="text-white">
              <i class="mdi mdi-credit-card-multiple"></i> Sales by Payment Method Analysis
            </h2>
            <p class="text-white opacity-75">Comprehensive payment method performance analysis and insights</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Advanced Filters Section -->
    <div class="container">
      <el-card class="m-b-20">
        <div slot="header" class="card-header">
          <h4><i class="mdi mdi-filter-variant"></i> Advanced Filters</h4>
          <el-button type="text" @click="resetFilters">
            <i class="el-icon-refresh"></i> Reset
          </el-button>
        </div>

        <el-form :inline="true" size="small" label-width="100px">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Date Range">
            <el-date-picker
                  v-model="filters.dateRange"
              type="daterange"
                  range-separator="to"
              start-placeholder="Start date"
              end-placeholder="End date"
                  format="MMM dd, yyyy"
                  value-format="yyyy-MM-dd"
                  :shortcuts="dateShortcuts"
                  @change="handleDateChange"
            />
          </el-form-item>
        </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Payment Methods">
            <el-select
                  v-model="filters.payment_method_id"
              multiple
                  filterable
                  placeholder="All Payment Methods"
                  @change="loadPaymentMethodData"
            >
              <el-option
                    v-for="method in paymentMethods"
                    :key="method.id"
                    :label="method.name"
                    :value="method.id"
                  />
            </el-select>
          </el-form-item>
        </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Employees">
            <el-select
                  v-model="filters.user_id"
              multiple
                  filterable
                  placeholder="All Employees"
                  @change="loadPaymentMethodData"
            >
              <el-option
                    v-for="user in users"
                    :key="user.id"
                    :label="user.name"
                    :value="user.id"
                  />
            </el-select>
          </el-form-item>
        </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Customers">
            <el-select
                  v-model="filters.customer_id"
              multiple
                  filterable
                  placeholder="All Customers"
                  @change="loadPaymentMethodData"
            >
              <el-option
                    v-for="customer in customers"
                    :key="customer.id"
                    :label="customer.name"
                    :value="customer.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Sort By">
                <el-select
                  v-model="filters.sort_by"
                  placeholder="Sort by"
                  @change="loadPaymentMethodData"
                >
                  <el-option label="Revenue (High to Low)" value="total_amount_desc"></el-option>
                  <el-option label="Revenue (Low to High)" value="total_amount_asc"></el-option>
                  <el-option label="Orders (High to Low)" value="total_orders_desc"></el-option>
                  <el-option label="Orders (Low to High)" value="total_orders_asc"></el-option>
                  <el-option label="Avg Transaction Value (High to Low)" value="average_transaction_value_desc"></el-option>
                  <el-option label="Avg Transaction Value (Low to High)" value="average_transaction_value_asc"></el-option>
                  <el-option label="Payment Method Name" value="payment_method_name"></el-option>
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Items Per Page">
                <el-select
                  v-model="filters.per_page"
                  placeholder="Items per page"
                  @change="loadPaymentMethodData"
                >
                  <el-option label="10" :value="10"></el-option>
                  <el-option label="25" :value="25"></el-option>
                  <el-option label="50" :value="50"></el-option>
                  <el-option label="100" :value="100"></el-option>
            </el-select>
          </el-form-item>
        </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item>
                <el-button type="primary" icon="el-icon-search" @click="loadPaymentMethodData" :loading="loading">
                  Search
                </el-button>
                <el-button type="success" icon="el-icon-download" @click="exportData" :loading="exporting">
                  Export
                </el-button>
              </el-form-item>
            </el-col>
      </el-row>
        </el-form>
      </el-card>

      <!-- Summary Statistics -->
      <el-row :gutter="20" class="m-b-20">
        <el-col :xs="24" :sm="12" :md="6" v-for="stat in summaryStats" :key="stat.title" class="m-b-20">
          <el-card :body-style="{ padding: '20px' }" class="stat-card">
            <div class="stat-content">
              <div class="stat-info">
                <h4 class="stat-title">{{ stat.title }}</h4>
                <h2 class="stat-value">{{ stat.value }}</h2>
                <div class="stat-comparison">
                  <span :class="['trend', stat.trend > 0 ? 'positive' : 'negative']">
                    <i :class="stat.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(stat.trend) }}%
                  </span>
                  <span class="comparison-text">vs previous period</span>
                </div>
              </div>
              <div class="stat-icon">
                <i :class="stat.icon"></i>
            </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- Loading State -->
      <div v-if="loading" class="text-center p-40">
        <i class="el-icon-loading" style="font-size: 24px;"></i>
        <p>Loading payment method analysis...</p>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Charts Section -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Payment Method Performance Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-bar"></i> Payment Method Performance</h4>
                <el-select v-model="chartView" size="mini" @change="updateChartData">
                  <el-option label="Revenue" value="revenue"></el-option>
                  <el-option label="Orders" value="orders"></el-option>
                  <el-option label="Avg Transaction Value" value="average_transaction_value"></el-option>
                </el-select>
              </div>
              <div class="chart-container">
                <bar-chart
                  v-if="paymentMethodData.length > 0"
                  :chart-data="paymentMethodChartData"
                  :chart-options="paymentMethodChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-bar" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Revenue Share Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-pie"></i> Revenue Share</h4>
                <el-switch v-model="showPercentage" active-text="%" inactive-text="Value"/>
              </div>
              <div class="chart-container">
                <doughnut-chart
                  v-if="paymentMethodData.length > 0"
                  :chart-data="revenueShareChartData"
                  :chart-options="revenueShareChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-pie" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Top Payment Methods List -->
        <el-row :gutter="20" class="m-b-20">
          <el-col :xs="24" :lg="8" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-trophy"></i> Top Payment Methods</h4>
              </div>
              <div class="top-payment-methods-list">
                <div
                  v-for="(method, index) in topPaymentMethods"
                  :key="method.payment_method_id"
                  class="payment-method-item"
                >
                  <div class="payment-method-rank">{{ index + 1 }}</div>
                  <div class="payment-method-info">
                    <div class="payment-method-name">{{ method.payment_method_name }}</div>
                    <div class="payment-method-stats">
                      <span class="revenue">{{ formatCurrency(method.total_amount) }}</span>
                      <span class="orders">{{ method.total_orders }} orders</span>
                    </div>
                    <el-progress
                      :percentage="(method.total_amount / Math.max(...topPaymentMethods.map(m => m.total_amount))) * 100"
                      :stroke-width="4"
                      :show-text="false"
                      :color="getProgressColor(index)"
                    />
                  </div>
                  <div class="payment-method-trend">
                    <span :class="['growth', method.growth_rate > 0 ? 'positive' : 'negative']">
                      <i :class="method.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(method.growth_rate) }}%
                    </span>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Trends Chart -->
          <el-col :xs="24" :lg="16" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-line"></i> Payment Method Trends</h4>
                <el-select v-model="trendPeriod" size="mini" @change="loadTrendsData">
                  <el-option label="Daily" value="daily"></el-option>
                  <el-option label="Weekly" value="weekly"></el-option>
                  <el-option label="Monthly" value="monthly"></el-option>
                </el-select>
              </div>
              <div class="chart-container">
                <line-chart
                  v-if="trendsData.length > 0"
                  :chart-data="trendsChartData"
                  :chart-options="trendsChartOptions"
                  :height="250"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-line" style="font-size: 48px;"></i>
                  <p>No trends data available</p>
            </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

        <!-- Payment Method Data Table -->
        <el-card>
          <div slot="header" class="card-header">
            <h4><i class="mdi mdi-table"></i> Payment Method Analysis</h4>
            <div class="header-actions">
              <el-tag type="info">{{ pagination.total }} payment methods found</el-tag>
              <el-button-group>
                <el-button size="mini" @click="toggleViewMode">
                  <i :class="viewMode === 'table' ? 'el-icon-menu' : 'el-icon-s-grid'"></i>
                  {{ viewMode === 'table' ? 'Grid' : 'Table' }}
                </el-button>
              </el-button-group>
            </div>
          </div>

          <!-- Table View -->
          <div v-if="viewMode === 'table'">
            <el-table
              :data="paymentMethodData"
              style="width: 100%"
              size="small"
              @sort-change="handleSortChange"
              @selection-change="handleSelectionChange"
            >
              <el-table-column type="selection" width="55" />
              <el-table-column prop="payment_method_name" label="Payment Method" width="200" />
              <el-table-column label="Revenue" width="120" sortable="custom">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.total_amount) }}
                </template>
              </el-table-column>
              <el-table-column label="Orders" width="100" sortable="custom">
                <template slot-scope="scope">
                  {{ scope.row.total_orders.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Avg Transaction Value" width="150">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.average_transaction_value) }}
                </template>
              </el-table-column>
              <el-table-column label="Unique Customers" width="120">
                <template slot-scope="scope">
                  {{ scope.row.unique_customers.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Unique Employees" width="120">
                <template slot-scope="scope">
                  {{ scope.row.unique_employees.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Efficiency Score" width="120">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.efficiency_score) }}
                </template>
              </el-table-column>
              <el-table-column label="Growth" width="100">
                <template slot-scope="scope">
                  <span :class="['growth', scope.row.growth_rate > 0 ? 'positive' : 'negative']">
                    <i :class="scope.row.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(scope.row.growth_rate) }}%
                  </span>
              </template>
              </el-table-column>
              <el-table-column label="Actions" width="120" fixed="right">
                <template slot-scope="scope">
                  <el-button-group>
                    <el-button
                      size="mini"
                      type="primary"
                      icon="el-icon-view"
                      @click="viewPaymentMethodDetails(scope.row)"
                    />
                    <el-button
                      size="mini"
                      type="success"
                      icon="el-icon-s-data"
                      @click="viewPaymentMethodTrends(scope.row)"
                    />
                  </el-button-group>
              </template>
              </el-table-column>
            </el-table>

            <!-- Pagination -->
            <div class="pagination-container">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pagination.current_page"
                :page-sizes="[10, 25, 50, 100]"
                :page-size="+pagination.per_page"
                layout="total, sizes, prev, pager, next, jumper"
                :total="pagination.total"
              />
            </div>
          </div>

          <!-- Grid View -->
          <div v-else class="grid-view">
            <el-row :gutter="20">
              <el-col
                v-for="method in paymentMethodData"
                :key="method.payment_method_id"
                :xs="24"
                :sm="12"
                :md="8"
                :lg="6"
                class="m-b-20"
              >
                <el-card :body-style="{ padding: '15px' }" class="payment-method-card">
                  <div class="payment-method-header">
                    <h4 class="payment-method-name">{{ method.payment_method_name }}</h4>
                    <el-tag :type="getPaymentMethodStatusType(method)" size="mini">
                      {{ getPaymentMethodStatusText(method) }}
                    </el-tag>
                  </div>

                  <div class="payment-method-info">
                    <div class="info-row">
                      <span class="label">Revenue:</span>
                      <span class="value amount">{{ formatCurrency(method.total_amount) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Orders:</span>
                      <span class="value">{{ method.total_orders.toLocaleString() }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Avg Transaction:</span>
                      <span class="value">{{ formatCurrency(method.average_transaction_value) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Unique Customers:</span>
                      <span class="value">{{ method.unique_customers.toLocaleString() }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Unique Employees:</span>
                      <span class="value">{{ method.unique_employees.toLocaleString() }}</span>
                    </div>
                  </div>

                  <div class="payment-method-footer">
                    <span class="growth" :class="method.growth_rate > 0 ? 'positive' : 'negative'">
                      <i :class="method.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(method.growth_rate) }}%
                    </span>
                    <div class="actions">
                      <el-button size="mini" type="primary" icon="el-icon-view" @click="viewPaymentMethodDetails(method)" />
                      <el-button size="mini" type="success" icon="el-icon-s-data" @click="viewPaymentMethodTrends(method)" />
                    </div>
                  </div>
                </el-card>
              </el-col>
            </el-row>
          </div>
        </el-card>
      </div>
    </div>

    <!-- Payment Method Details Dialog -->
    <el-dialog
      title="Payment Method Details"
      :visible.sync="paymentMethodDetailsVisible"
      width="70%"
      :before-close="handleCloseDialog"
    >
      <div v-if="selectedPaymentMethodDetails" class="payment-method-details">
        <el-row :gutter="20">
          <el-col :span="12">
            <h4>Payment Method Information</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Name">{{ selectedPaymentMethodDetails.payment_method.name }}</el-descriptions-item>
              <el-descriptions-item label="Description">{{ selectedPaymentMethodDetails.payment_method.description || 'No description' }}</el-descriptions-item>
              <el-descriptions-item label="Created">{{ formatDate(selectedPaymentMethodDetails.payment_method.created_at) }}</el-descriptions-item>
            </el-descriptions>
          </el-col>

          <el-col :span="12">
            <h4>Usage Statistics</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Total Revenue">{{ formatCurrency(selectedPaymentMethodDetails.usage_stats.total_amount) }}</el-descriptions-item>
              <el-descriptions-item label="Total Orders">{{ selectedPaymentMethodDetails.usage_stats.total_orders.toLocaleString() }}</el-descriptions-item>
              <el-descriptions-item label="Avg Transaction Value">{{ formatCurrency(selectedPaymentMethodDetails.usage_stats.average_transaction_value) }}</el-descriptions-item>
              <el-descriptions-item label="Unique Employees">{{ selectedPaymentMethodDetails.usage_stats.unique_employees.toLocaleString() }}</el-descriptions-item>
              <el-descriptions-item label="Unique Customers">{{ selectedPaymentMethodDetails.usage_stats.unique_customers.toLocaleString() }}</el-descriptions-item>
            </el-descriptions>
          </el-col>
      </el-row>

        <el-divider content-position="left">Top Employees</el-divider>
        <el-table :data="selectedPaymentMethodDetails.top_employees || []" size="small">
          <el-table-column prop="user_name" label="Employee" />
          <el-table-column prop="total_orders" label="Orders" width="80" />
          <el-table-column prop="total_amount" label="Revenue" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.total_amount) }}
            </template>
          </el-table-column>
          <el-table-column prop="average_transaction_value" label="Avg Transaction" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.average_transaction_value) }}
            </template>
          </el-table-column>
        </el-table>

        <el-divider content-position="left">Top Customers</el-divider>
        <el-table :data="selectedPaymentMethodDetails.top_customers || []" size="small">
          <el-table-column prop="customer_name" label="Customer" />
          <el-table-column prop="total_orders" label="Orders" width="80" />
          <el-table-column prop="total_amount" label="Revenue" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.total_amount) }}
            </template>
          </el-table-column>
          <el-table-column prop="average_transaction_value" label="Avg Transaction" width="120">
            <template slot-scope="scope">
              {{ formatCurrency(scope.row.average_transaction_value) }}
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="paymentMethodDetailsVisible = false">Close</el-button>
        <el-button type="primary" @click="viewPaymentMethodTrends(selectedPaymentMethod)">View Trends</el-button>
        <el-button type="success" @click="exportPaymentMethodReport(selectedPaymentMethod)">Export Report</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import BarChart from '@/components/_Partials/Charts/BarChart.vue'
import DoughnutChart from '@/components/_Partials/Charts/DoughnutChart.vue'
import LineChart from '@/components/_Partials/Charts/LineChart.vue'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'SalesByPaymentMethod',
  components: {
    BarChart,
    DoughnutChart,
    LineChart
  },
  mixins: [ErrorHandlingMixin],
  data () {
    return {
      loading: false,
      exporting: false,
      viewMode: 'table',
      paymentMethodDetailsVisible: false,
      selectedPaymentMethod: null,
      selectedPaymentMethodDetails: null,
      selectedPaymentMethods: [],
      chartView: 'revenue',
      showPercentage: false,
      trendPeriod: 'monthly',
      paymentMethodData: [],
      trendsData: [],
      users: [],
      customers: [],
      paymentMethods: [],
      filters: {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        payment_method_id: [],
        user_id: [],
        customer_id: [],
        sort_by: 'total_amount_desc',
        per_page: 25
      },
      pagination: {
        current_page: 1,
        per_page: 25,
        total: 0,
        last_page: 1
      },
      dateShortcuts: [{
        text: 'Last 7 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 6)
          return [start, end]
        }
      }, {
        text: 'Last 30 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 29)
          return [start, end]
        }
      }, {
        text: 'Last 3 months',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setMonth(start.getMonth() - 3)
          return [start, end]
        }
      }, {
        text: 'This year',
        value: () => {
          const end = new Date()
          const start = new Date(new Date().getFullYear(), 0, 1)
          return [start, end]
        }
      }]
    }
  },
  computed: {
    summaryStats () {
      const totalRevenue = this.paymentMethodData.reduce((sum, m) => sum + m.total_amount, 0)
      const totalOrders = this.paymentMethodData.reduce((sum, m) => sum + m.total_orders, 0)
      const avgTransactionValue = this.paymentMethodData.length > 0 ? (totalRevenue / totalOrders) : 0
      const uniqueCustomers = this.paymentMethodData.reduce((sum, m) => sum + m.unique_customers, 0)
      const avgGrowth = this.paymentMethodData.length > 0 ? (this.paymentMethodData.reduce((sum, m) => sum + m.growth_rate, 0) / this.paymentMethodData.length) : 0
      return [
        {
          title: 'Total Revenue',
          value: this.formatCurrency(totalRevenue),
          trend: 12.5,
          icon: 'mdi mdi-currency-fra'
        },
        {
          title: 'Total Orders',
          value: totalOrders.toLocaleString(),
          trend: 8.3,
          icon: 'mdi mdi-receipt'
        },
        {
          title: 'Avg Transaction Value',
          value: this.formatCurrency(avgTransactionValue),
          trend: 5.2,
          icon: 'mdi mdi-chart-line'
        },
        {
          title: 'Unique Customers',
          value: uniqueCustomers.toLocaleString(),
          trend: avgGrowth,
          icon: 'mdi mdi-account-group'
        }
      ]
    },
    topPaymentMethods () {
      return this.paymentMethodData.slice(0, 5)
    },
    paymentMethodChartData () {
      const labels = this.paymentMethodData.slice(0, 10).map(m => m.payment_method_name)
      const data = this.paymentMethodData.slice(0, 10).map(m => {
        switch (this.chartView) {
          case 'revenue':
            return m.total_amount
          case 'orders':
            return m.total_orders
          case 'average_transaction_value':
            return m.average_transaction_value
          default:
            return m.total_amount
        }
      })
      return {
        labels: labels,
        datasets: [{
          label: this.chartView.charAt(0).toUpperCase() + this.chartView.slice(1),
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: this.getChartColors(data.length),
          borderWidth: 1
        }]
      }
    },
    paymentMethodChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                if (this.chartView === 'revenue' || this.chartView === 'average_transaction_value') {
                  return this.formatCurrency(value)
                }
                return value.toLocaleString()
              }.bind(this)
            }
          }
        }
      }
    },
    revenueShareChartData () {
      const labels = this.paymentMethodData.slice(0, 8).map(m => m.payment_method_name)
      const data = this.paymentMethodData.slice(0, 8).map(m => {
        if (this.showPercentage) {
          const total = this.paymentMethodData.reduce((sum, i) => sum + i.total_amount, 0)
          return ((m.total_amount / total) * 100).toFixed(1)
        }
        return m.total_amount
      })
      return {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: '#fff',
          borderWidth: 2
        }]
      }
    },
    revenueShareChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' }
        }
      }
    },
    trendsChartData () {
      return {
        labels: this.trendsData.map(item => item.period),
        datasets: this.paymentMethodData.slice(0, 5).map((m, index) => ({
          label: m.payment_method_name,
          data: this.trendsData.filter(t => t.payment_method_id === m.payment_method_id).map(t => t.total_amount),
          borderColor: this.getChartColors(5)[index],
          backgroundColor: this.getChartColors(5)[index] + '20',
          tension: 0.4
        }))
      }
    },
    trendsChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return this.formatCurrency(value)
              }.bind(this)
            }
          }
        }
      }
    }
  },
  mounted () {
    this.loadInitialData()
  },
  methods: {
    getDefaultStartDate () {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },
    getDefaultEndDate () {
      return new Date().toISOString().split('T')[0]
    },
    handleDateChange (dates) {
      if (dates && dates.length === 2) {
        this.filters.start_date = dates[0]
        this.filters.end_date = dates[1]
        this.pagination.current_page = 1
        this.loadPaymentMethodData()
      }
    },
    resetFilters () {
      this.filters = {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        payment_method_id: [],
        user_id: [],
        customer_id: [],
        sort_by: 'total_amount_desc',
        per_page: 25
      }
      this.pagination.current_page = 1
      this.loadPaymentMethodData()
    },
    async loadInitialData () {
      try {
        await Promise.all([
          this.loadUsers(),
          this.loadCustomers(),
          this.loadPaymentMethods()
        ])
        await this.loadPaymentMethodData()
      } catch (error) {
        this.handleError(error, 'Failed to load initial data')
      }
    },
    async loadPaymentMethodData () {
      this.loading = true
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        }
        const response = await this.$reportService.getPaymentMethodPerformance(params)
        if (response.status === 'success') {
          this.paymentMethodData = response.data
          this.pagination = {
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          }
        }
      } catch (error) {
        this.handleError(error, 'Failed to load payment method data')
      } finally {
        this.loading = false
      }
    },
    async loadTrendsData () {
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          period: this.trendPeriod,
          per_page: 1000,
          page: 1
        }
        const response = await this.$reportService.getPaymentMethodTrends(params)
        if (response.status === 'success') {
          this.trendsData = response.data
        }
      } catch (error) {
        console.error('Error loading trends data:', error)
      }
    },
    async loadUsers () {
      try {
        this.users = await this.$userService.getUsers()
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },
    async loadCustomers () {
      try {
        this.customers = await this.$customerService.getCustomers()
      } catch (error) {
        console.error('Error loading customers:', error)
      }
    },
    async loadPaymentMethods () {
      try {
        this.paymentMethods = await this.$paymentMethodService.getMethods()
      } catch (error) {
        console.error('Error loading payment methods:', error)
      }
    },
    async exportData () {
      this.exporting = true
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          dl: 'pdf'
        }
        const response = await this.$reportService.getPaymentMethodPerformance(params)
        if (response.url) {
          window.open(response.url, '_blank')
        }
      } catch (error) {
        this.handleError(error, 'Failed to export data')
      } finally {
        this.exporting = false
      }
    },
    updateChartData () {
      // Chart data will be updated automatically through computed properties
    },
    handleSortChange ({ column, prop, order }) {
      // Handle table sorting
      this.loadPaymentMethodData()
    },
    handleSelectionChange (selection) {
      this.selectedPaymentMethods = selection
    },
    handleSizeChange (size) {
      this.pagination.per_page = size
      this.loadPaymentMethodData()
    },
    handleCurrentChange (page) {
      this.pagination.current_page = page
      this.loadPaymentMethodData()
    },
    toggleViewMode () {
      this.viewMode = this.viewMode === 'table' ? 'grid' : 'table'
    },
    viewPaymentMethodDetails (method) {
      this.selectedPaymentMethod = method
      this.paymentMethodDetailsVisible = true
      this.loadPaymentMethodDetails(method)
    },
    async loadPaymentMethodDetails (method) {
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const response = await this.$reportService.getPaymentMethodDetails({
          payment_method_id: method.payment_method_id,
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1])
        })
        if (response.status === 'success') {
          this.selectedPaymentMethodDetails = response.data
        }
      } catch (error) {
        this.handleError(error, 'Failed to load payment method details')
      }
    },
    viewPaymentMethodTrends (method) {
      // Could open a modal or navigate to a trends view
      console.log('Viewing trends for payment method:', method.payment_method_name)
    },
    exportPaymentMethodReport (method) {
      // Implement payment method-specific report export
      console.log('Exporting report for payment method:', method.payment_method_name)
    },
    handleCloseDialog () {
      this.paymentMethodDetailsVisible = false
      this.selectedPaymentMethod = null
      this.selectedPaymentMethodDetails = null
    },
    getPaymentMethodStatusType (method) {
      if (method.growth_rate > 10) return 'success'
      if (method.growth_rate > 0) return 'warning'
      return 'danger'
    },
    getPaymentMethodStatusText (method) {
      if (method.growth_rate > 10) return 'High Growth'
      if (method.growth_rate > 0) return 'Growing'
      return 'Declining'
    },
    getProgressColor (index) {
      const colors = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399']
      return colors[index % colors.length]
    },
    getChartColors (count) {
      const colors = [
        '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
        '#9C27B0', '#FF9800', '#795548', '#607D8B', '#FF5722'
      ]
      return colors.slice(0, count)
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'XAF'
      }).format(value)
    },
    formatDate (dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.sales-by-payment-method {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.stat-card {
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.stat-info {
  flex: 1;
}

.stat-title {
  color: #606266;
  font-size: 14px;
  margin: 0 0 8px 0;
}

.stat-value {
  color: #303133;
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 8px 0;
}

.stat-comparison {
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend {
  font-size: 12px;
  font-weight: bold;
}

.trend.positive {
  color: #67C23A;
}

.trend.negative {
  color: #F56C6C;
}

.comparison-text {
  font-size: 12px;
  color: #909399;
}

.stat-icon {
  font-size: 32px;
  color: #409EFF;
  opacity: 0.8;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chart-container {
  position: relative;
  height: 300px;
}

.top-payment-methods-list {
  max-height: 300px;
  overflow-y: auto;
}

.payment-method-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #EBEEF5;
}

.payment-method-item:last-child {
  border-bottom: none;
}

.payment-method-rank {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #409EFF;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-right: 12px;
}

.payment-method-info {
  flex: 1;
  margin-right: 12px;
}

.payment-method-name {
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.payment-method-stats {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.payment-method-trend {
  text-align: right;
  font-size: 12px;
}

.growth {
  font-weight: bold;
}

.growth.positive {
  color: #67C23A;
}

.growth.negative {
  color: #F56C6C;
}

.grid-view {
  margin-top: 20px;
}

.payment-method-card {
  transition: all 0.3s ease;
}

.payment-method-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.payment-method-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.payment-method-name {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.payment-method-info {
  margin-bottom: 15px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.label {
  font-size: 12px;
  color: #909399;
}

.value {
  font-size: 12px;
  color: #303133;
  font-weight: 500;
}

.value.amount {
  color: #409EFF;
  font-weight: bold;
}

.payment-method-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #EBEEF5;
}

.actions {
  display: flex;
  gap: 5px;
}

.pagination-container {
  margin-top: 20px;
  text-align: center;
}

.text-muted {
  color: #909399;
}

.p-40 {
  padding: 40px;
}

.m-b-20 {
  margin-bottom: 20px;
}

.m-b-30 {
  margin-bottom: 30px;
}

.p-b-30 {
  padding-bottom: 30px;
}

.p-t-30 {
  padding-top: 30px;
}
</style>
